import { Dispatch, SetStateAction, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { DELETE_LISTING, SAVE_LISTING } from '@zoopla/listing-search-queries';
import { Action, AnalyticsTaxonomy } from '@zoopla/listing-search-context';
import { useQuery } from '@apollo/client';
import { USER_FAVOURITES_QUERY } from '@zoopla/listing-search-queries';
import triggerTracking from '@blueprint/tracking';

const favouriteTracking = (
    isFavourite: boolean,
    trackingCategory: 'search results' | 'map_search_results'
) => {
    triggerTracking({
        event: 'GAEvent',
        eventCategory: trackingCategory,
        eventAction: isFavourite ? 'remove' : 'save',
        eventLabel: 'save listing'
    });
};

export const useSaveListing = (
    dispatch: Dispatch<Action>,
    favouriteListings: string[],
    isLoggedIn: boolean,
    analyticsTaxonomy: AnalyticsTaxonomy | undefined,
    setAuthHeader: Dispatch<SetStateAction<string | undefined>>,
    openAuthModal: (triggerOrigin: React.RefObject<HTMLElement> | null) => void,
    trackingCategory: 'search results' | 'map_search_results'
) => {
    const [saveListingMutation] = useMutation(SAVE_LISTING);
    const [deleteListingMutation] = useMutation(DELETE_LISTING);

    useQuery(USER_FAVOURITES_QUERY, {
        onCompleted: (response) => {
            dispatch({
                type: 'SET_LISTINGS_USER_FAVOURITES',
                payload: {
                    favourites: response.favourites
                }
            });
        }
    });

    const saveToggle = useCallback(
        async (
            id: string,
            isFavourite: boolean,
            onSuccess: (args: { isSaved: boolean }) => void
        ) => {
            const immediateAction = isFavourite
                ? 'REMOVE_FAVOURITE_LISTING'
                : 'ADD_FAVOURITE_LISTING';
            const rollbackAction = isFavourite
                ? 'ADD_FAVOURITE_LISTING'
                : 'REMOVE_FAVOURITE_LISTING';
            const propertyToCheck = isFavourite
                ? 'deleteFavouriteListing'
                : 'saveFavouriteListing';
            const apiCall = (id: string) => {
                return isFavourite
                    ? deleteListingMutation({ variables: { id } })
                    : saveListingMutation({ variables: { id } });
            };

            dispatch({
                type: immediateAction,
                payload: id
            });

            try {
                const { data } = await apiCall(id);
                if (!data?.[propertyToCheck]) {
                    throw new Error('Update unsuccessful');
                }
            } catch (e) {
                dispatch({
                    type: rollbackAction,
                    payload: id
                });
            }
            onSuccess?.({ isSaved: !isFavourite });
            return Promise.resolve(true);
        },
        [deleteListingMutation, saveListingMutation, dispatch]
    );

    const authenticateSaveToggle = async (
        listingId: string,
        onSuccess: ({ isSaved }: { isSaved: boolean }) => void
    ) => {
        const isFavourite = favouriteListings.includes(listingId);

        favouriteTracking(isFavourite, trackingCategory);

        if (!isLoggedIn) {
            const segment = analyticsTaxonomy
                ? {
                      page: analyticsTaxonomy.page,
                      section: analyticsTaxonomy.section,
                      activity: analyticsTaxonomy.activity
                  }
                : undefined;

            dispatch({
                type: 'SET_ACTION_AFTER_AUTH',
                payload: {
                    action: () => {
                        saveToggle(listingId, isFavourite, onSuccess);
                    },
                    eventLabel: 'save_listing',
                    segment
                }
            });
            setAuthHeader(undefined);
            openAuthModal(null);
            return false;
        }

        return saveToggle(listingId, isFavourite, onSuccess);
    };

    return authenticateSaveToggle;
};
